import { Component, inject, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { LayoutService } from '@app/core/layout/service/app.layout.service';

@Component({
  selector: 'dynamic-logo',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      #logoContainer
      class="layout-topbar-logo-content cursor-pointer"
      [innerHTML]="!layoutService.isDarkTheme() ? sanitizedLogoHtmlLight : sanitizedLogoHtmlDark"
      (click)="navigateToHome()"
    ></div>
  `,
  styles: [
    `
      .layout-topbar-logo-content {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
      }

      .layout-topbar-logo-content img {
        width: 100%;
      }

      .layout-topbar-logo-content span {
        font-weight: bold;
        font-size: 1.25rem;
      }

      .layout-topbar-logo-content:hover {
        opacity: 0.8;
        transition: opacity 0.2s ease;
      }
    `,
  ],
})
export class DynamicLogoComponent implements OnInit, OnDestroy {
  private appSettingsService = inject(AppSettingsService);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private subscription?: Subscription;
  layoutService = inject(LayoutService);

  @ViewChild('logoContainer', { static: true }) logoContainer!: ElementRef;

  sanitizedLogoHtmlLight: SafeHtml = '';
  sanitizedLogoHtmlDark: SafeHtml = '';

  ngOnInit(): void {
    this.subscription = this.appSettingsService.getSettings().subscribe((settings) => {
      if (settings) {
        this.updateLogo(settings.logoHtmlLight, settings.logoHtmlDark);
      } else {
        // Load settings if not already loaded
        this.appSettingsService.loadSettings().subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  private updateLogo(logoHtmlLight: string, logoHtmlDark: string): void {
    // Basic sanitization to prevent XSS attacks
    const sanitizedHtmlLight = this.basicSanitize(logoHtmlLight);
    const sanitizedHtmlDark = this.basicSanitize(logoHtmlDark);
    this.sanitizedLogoHtmlLight = this.sanitizer.bypassSecurityTrustHtml(sanitizedHtmlLight);
    this.sanitizedLogoHtmlDark = this.sanitizer.bypassSecurityTrustHtml(sanitizedHtmlDark);
  }

  private basicSanitize(html: string): string {
    // Remove script tags and common XSS vectors
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/on\w+='[^']*'/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/data:/gi, ''); // Remove data URLs for extra security
  }
}
